import { useEffect, useRef, useState } from 'react';
import { usersApi, departmentsApi, businessUnitsApi, divisionsApi, gradesApi, categoriesApi, goalLibraryTemplatesApi } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { SearchableSelect } from '../components/ui/searchable-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Upload, Search, Loader2, Users, Download, Calendar, X } from 'lucide-react';

const roleColors = {
  admin: 'bg-purple-100 text-purple-800',
  hr: 'bg-blue-100 text-blue-800',
  manager: 'bg-green-100 text-green-800',
  employee: 'bg-gray-100 text-gray-800',
};

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [unitFilter, setUnitFilter] = useState('all');
  const [divisionFilter, setDivisionFilter] = useState('all');
  const [gradeFilter, setGradeFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [managers, setManagers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [grades, setGrades] = useState([]);
  const [categories, setCategories] = useState([]);
  const [templates, setTemplates] = useState([]);

  const [formData, setFormData] = useState({
    employee_number: '',
    name: '',
    email: '',
    password: '',
    role: 'employee',
    department: '',
    business_unit: '',
    division: '',
    function: '',
    grade: '',
    category: '',
    designation: '',
    manager_employee_number: '',
    department_head_id: '',
    department_head_name: '',
    department_head_email: '',
    joining_date: '',
    pre_approved_goals: false,
    assigned_template_id: null,
  });

  const [importData, setImportData] = useState('');
  const [importFileName, setImportFileName] = useState('');
  const importFileInputRef = useRef(null);

  useEffect(() => {
    loadUsers();
    loadManagers();
    loadDepartments();
    loadBusinessUnits();
    loadDivisions();
    loadGrades();
    loadCategories();
    loadTemplates();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUsers = async () => {
    try {
      const params = {};
      if (roleFilter !== 'all') params.role = roleFilter;
      if (searchTerm) params.search = searchTerm;
      
      const response = await usersApi.getAll(params);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadManagers = async () => {
    try {
      const response = await usersApi.getAll({ role: 'manager' });
      setManagers(response.data);
    } catch (error) {
      console.error('Failed to load managers:', error);
    }
  };

  const loadDepartments = async () => {
    try {
      const response = await departmentsApi.getAll();
      setDepartments(response.data);
    } catch (error) {
      console.error('Failed to load departments:', error);
    }
  };

  const loadBusinessUnits = async () => {
    try {
      const response = await businessUnitsApi.getAll();
      setBusinessUnits(response.data);
    } catch (error) {
      console.error('Failed to load business units:', error);
    }
  };

  const loadDivisions = async () => {
    try {
      const response = await divisionsApi.getAll();
      setDivisions(response.data);
    } catch (error) {
      console.error('Failed to load divisions:', error);
    }
  };

  const loadGrades = async () => {
    try {
      const response = await gradesApi.getAll();
      setGrades(response.data);
    } catch (error) {
      console.error('Failed to load grades:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await categoriesApi.getAll();
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await goalLibraryTemplatesApi.getAll();
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      loadUsers();
    }, 300);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, roleFilter, departmentFilter, unitFilter, divisionFilter, gradeFilter, categoryFilter]);

  // Filter users locally for advanced filters
  const filteredUsers = users.filter(user => {
    if (departmentFilter !== 'all' && user.department !== departmentFilter) return false;
    if (unitFilter !== 'all' && user.business_unit !== unitFilter) return false;
    if (divisionFilter !== 'all' && user.division !== divisionFilter) return false;
    if (gradeFilter !== 'all' && user.grade !== gradeFilter) return false;
    if (categoryFilter !== 'all' && user.category !== categoryFilter) return false;
    return true;
  });

  const clearFilters = () => {
    setSearchTerm('');
    setRoleFilter('all');
    setDepartmentFilter('all');
    setUnitFilter('all');
    setDivisionFilter('all');
    setGradeFilter('all');
    setCategoryFilter('all');
  };

  const hasActiveFilters = searchTerm || roleFilter !== 'all' || departmentFilter !== 'all' || 
    unitFilter !== 'all' || divisionFilter !== 'all' || gradeFilter !== 'all' || categoryFilter !== 'all';

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.pre_approved_goals && !formData.assigned_template_id) {
      toast.error('Please select a template');
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        assigned_template_id: formData.pre_approved_goals ? formData.assigned_template_id : null,
      };

      if (editingUser) {
        const updateData = { ...payload };
        if (!updateData.password) delete updateData.password;
        delete updateData.employee_number; // Can't update employee number

        await usersApi.update(editingUser.id, updateData);
        toast.success('User updated successfully');
      } else {
        await usersApi.create(payload);
        toast.success('User created successfully');
      }

      setDialogOpen(false);
      resetForm();
      loadUsers();
    } catch (error) {
      console.error('Failed to save user:', error);
      toast.error(error.response?.data?.detail || 'Failed to save user');
    } finally {
      setSubmitting(false);
    }
  };

  // Auto-populate manager_employee_number when manager is selected
  const handleManagerChange = (managerEmpNumber) => {
    setFormData({
      ...formData,
      manager_employee_number: managerEmpNumber === "none" ? "" : managerEmpNumber
    });
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      employee_number: user.employee_number,
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      department: user.department || '',
      business_unit: user.business_unit || '',
      division: user.division || '',
      function: user.function || '',
      grade: user.grade || '',
      category: user.category || '',
      designation: user.designation || '',
      manager_employee_number: user.manager_employee_number || '',
      department_head_id: user.department_head_id || '',
      department_head_name: user.department_head_name || '',
      department_head_email: user.department_head_email || '',
      joining_date: user.joining_date || '',
      pre_approved_goals: user.pre_approved_goals || false,
      assigned_template_id: user.assigned_template_id || null,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (userId) => {
    if (!confirm('Are you sure you want to delete this user?')) return;

    try {
      await usersApi.delete(userId);
      toast.success('User deleted successfully');
      loadUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  // Parse one CSV line, honoring double-quoted fields that may contain commas
  // (e.g. "Doe, John") and escaped quotes ("").
  const parseCsvLine = (line) => {
    const values = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        values.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current);
    return values.map(v => v.trim());
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setImportData(String(reader.result || ''));
      setImportFileName(file.name);
    };
    reader.onerror = () => {
      toast.error('Failed to read file');
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleImport = async () => {
    setSubmitting(true);
    try {
      // Normalize Windows/Mac line endings and drop blank lines so a trailing
      // newline or CRLF (\r\n) export does not corrupt the last column.
      const lines = importData
        .replace(/^﻿/, '')          // strip UTF-8 BOM
        .replace(/\r\n?/g, '\n')         // CRLF / CR -> LF
        .split('\n')
        .filter(line => line.trim() !== '');

      if (lines.length < 2) {
        toast.error('Paste a header row plus at least one data row.');
        return;
      }

      // Detect semicolon-delimited files (common when Excel is in Arabic/European locale)
      if (!lines[0].includes(',') && lines[0].includes(';')) {
        toast.error('Your CSV uses semicolons (;) as separators. Please open the file in a text editor and replace all semicolons with commas, then try again.');
        return;
      }

      const headers = parseCsvLine(lines[0]).map(h => h.toLowerCase().replace(/\s+/g, '_'));

      const users = lines.slice(1).map(line => {
        const values = parseCsvLine(line);
        const user = {};
        headers.forEach((header, index) => {
          user[header] = values[index] !== undefined ? values[index] : '';
        });
        return user;
      });

      const response = await usersApi.import(users);
      const { imported = 0, created = 0, updated = 0, errors = [] } = response.data;
      const detail = `${created} created, ${updated} updated`;

      if (errors.length > 0) {
        const firstErr = errors[0];
        const errSummary = `Row ${firstErr.row}: ${firstErr.error}`;
        toast.warning(`${detail} — ${errors.length} row(s) failed. ${errors.length === 1 ? errSummary : 'See console for details.'}`);
        console.warn('Import errors:', errors);
      } else {
        toast.success(`Import complete: ${detail} (${imported} total)`);
      }

      setImportDialogOpen(false);
      setImportData('');
      setImportFileName('');
      loadUsers();
    } catch (error) {
      const serverMsg = error.response?.data?.detail || error.response?.data?.message || error.message;
      const status = error.response?.status;
      console.error('Import failed:', error.response?.data || error);
      toast.error(serverMsg ? `Import failed (${status}): ${serverMsg}` : 'Failed to import users');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      employee_number: '',
      name: '',
      email: '',
      password: '',
      role: 'employee',
      department: '',
      business_unit: '',
      division: '',
      function: '',
      grade: '',
      category: '',
      designation: '',
      manager_employee_number: '',
      department_head_id: '',
      department_head_name: '',
      department_head_email: '',
      joining_date: '',
      pre_approved_goals: false,
      assigned_template_id: null,
    });
    setEditingUser(null);
  };

  const downloadTemplate = () => {
    const template = 'employee_number,name,email,role,business_unit,division,department,function,grade,category,designation,manager_employee_number,department_head_id,department_head_name,department_head_email,joining_date,password,template_name\nEMP005,John Doe,john@company.com,employee,Retail,Operations,Sales,HR,Grade 5,Permanent,Sales Executive,EMP003,EMP001,Jane Manager,jane@company.com,2024-01-15,Welcome@123,';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'users_import_template.csv';
    a.click();
  };

  const handleExportUsers = async () => {
    try {
      const response = await usersApi.export();
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Users exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export users');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">User Management</h1>
          <p className="text-muted-foreground mt-1">
            Manage employees, managers, and HR staff
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={handleExportUsers} data-testid="export-users-btn">
            <Download className="h-4 w-4 mr-2" />
            Export Users
          </Button>
          <Dialog open={importDialogOpen} onOpenChange={(open) => {
            setImportDialogOpen(open);
            if (!open) {
              setImportData('');
              setImportFileName('');
            }
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="import-users-btn">
                <Upload className="h-4 w-4 mr-2" />
                Import Users
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Import Users</DialogTitle>
                <DialogDescription>
                  Select a CSV file or paste CSV data to bulk import users
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-muted-foreground">
                    Required: employee_number, name, email, role. Optional: business_unit, division, department, function, grade, category, designation, template_name, etc.
                  </p>
                  <Button variant="link" size="sm" onClick={downloadTemplate}>
                    <Download className="h-4 w-4 mr-1" />
                    Download Template
                  </Button>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    ref={importFileInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    className="hidden"
                    onChange={handleFileSelect}
                    data-testid="import-file-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => importFileInputRef.current?.click()}
                    data-testid="import-choose-file-btn"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Choose File
                  </Button>
                  {importFileName && (
                    <span className="text-sm text-muted-foreground truncate">{importFileName}</span>
                  )}
                </div>
                <textarea
                  className="w-full h-48 p-3 text-sm border rounded-md font-mono"
                  placeholder="...or paste CSV data here"
                  value={importData}
                  onChange={(e) => {
                    setImportData(e.target.value);
                    setImportFileName('');
                  }}
                  data-testid="import-data-input"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setImportDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleImport} disabled={!importData.trim() || submitting} data-testid="confirm-import-btn">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Import
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-user-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
                <DialogDescription>
                  {editingUser ? 'Update user information' : 'Create a new user account'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit}>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="employee_number">Employee ID</Label>
                      <Input
                        id="employee_number"
                        value={formData.employee_number}
                        onChange={(e) => setFormData({ ...formData, employee_number: e.target.value })}
                        placeholder="EMP001"
                        required
                        disabled={!!editingUser}
                        data-testid="user-employee-number-input"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="name">Full Name</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="John Doe"
                        required
                        data-testid="user-name-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="john@company.com"
                        required
                        data-testid="user-email-input"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="password">
                        Password {editingUser && '(leave blank to keep current)'}
                      </Label>
                      <Input
                        id="password"
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder="••••••••"
                        required={!editingUser}
                        data-testid="user-password-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="role">Role</Label>
                      <SearchableSelect
                        value={formData.role}
                        onValueChange={(value) => setFormData({ ...formData, role: value })}
                        placeholder="Select role"
                        data-testid="user-role-select"
                        options={[
                          { value: "employee", label: "Employee" },
                          { value: "manager", label: "Manager" },
                          { value: "hr", label: "HR" },
                          { value: "admin", label: "Admin" },
                        ]}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="manager">Manager</Label>
                      <SearchableSelect
                        value={formData.manager_employee_number || "none"}
                        onValueChange={handleManagerChange}
                        placeholder="Select manager"
                        searchPlaceholder="Search managers..."
                        data-testid="user-manager-select"
                        options={[
                          { value: "none", label: "No Manager" },
                          ...managers.map((manager) => ({
                            value: manager.employee_number,
                            label: `${manager.name} (${manager.employee_number})`,
                          })),
                        ]}
                      />
                    </div>
                  </div>
                  {/* Auto-populated Manager Employee Number */}
                  {formData.manager_employee_number && (
                    <div className="grid gap-2">
                      <Label htmlFor="manager_employee_number">Manager Employee Number</Label>
                      <Input
                        id="manager_employee_number"
                        value={formData.manager_employee_number}
                        readOnly
                        disabled
                        className="bg-muted"
                        data-testid="user-manager-emp-number"
                      />
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="business_unit">Unit</Label>
                      <SearchableSelect
                        value={formData.business_unit || "none"}
                        onValueChange={(value) => setFormData({ ...formData, business_unit: value === "none" ? "" : value })}
                        placeholder="Select unit"
                        searchPlaceholder="Search units..."
                        data-testid="user-business-unit-select"
                        options={[
                          { value: "none", label: "Select Unit" },
                          ...businessUnits.map((bu) => ({ value: bu.name, label: bu.name })),
                        ]}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="division">Division</Label>
                      <SearchableSelect
                        value={formData.division || "none"}
                        onValueChange={(value) => setFormData({ ...formData, division: value === "none" ? "" : value })}
                        placeholder="Select division"
                        searchPlaceholder="Search divisions..."
                        data-testid="user-division-select"
                        options={[
                          { value: "none", label: "Select Division" },
                          ...divisions.map((d) => ({ value: d.name, label: d.name })),
                        ]}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="department">Department</Label>
                      <SearchableSelect
                        value={formData.department || "none"}
                        onValueChange={(value) => setFormData({ ...formData, department: value === "none" ? "" : value })}
                        placeholder="Select department"
                        searchPlaceholder="Search departments..."
                        data-testid="user-department-select"
                        options={[
                          { value: "none", label: "Select Department" },
                          ...departments.map((dept) => ({ value: dept.name, label: dept.name })),
                        ]}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="function">Function</Label>
                      <Input
                        id="function"
                        value={formData.function}
                        onChange={(e) => setFormData({ ...formData, function: e.target.value })}
                        placeholder="e.g., Human Resources"
                        data-testid="user-function-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="grade">Grade</Label>
                      <SearchableSelect
                        value={formData.grade || "none"}
                        onValueChange={(value) => setFormData({ ...formData, grade: value === "none" ? "" : value })}
                        placeholder="Select grade"
                        searchPlaceholder="Search grades..."
                        data-testid="user-grade-select"
                        options={[
                          { value: "none", label: "Select Grade" },
                          ...grades.map((g) => ({ value: g.name, label: g.name })),
                        ]}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="category">Category</Label>
                      <SearchableSelect
                        value={formData.category || "none"}
                        onValueChange={(value) => setFormData({ ...formData, category: value === "none" ? "" : value })}
                        placeholder="Select category"
                        searchPlaceholder="Search categories..."
                        data-testid="user-category-select"
                        options={[
                          { value: "none", label: "Select Category" },
                          ...categories.map((c) => ({ value: c.name, label: c.name })),
                        ]}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="designation">Designation</Label>
                      <Input
                        id="designation"
                        value={formData.designation}
                        onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                        placeholder="Software Engineer"
                        data-testid="user-designation-input"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="joining_date">Joining Date</Label>
                      <Input
                        id="joining_date"
                        type="date"
                        value={formData.joining_date}
                        onChange={(e) => setFormData({ ...formData, joining_date: e.target.value })}
                        data-testid="user-joining-date-input"
                      />
                    </div>
                  </div>
                  
                  {/* Pre-Approved Goals */}
                  <div className="border-t pt-4 mt-2">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="pre_approved_goals"
                        checked={formData.pre_approved_goals || false}
                        onChange={(e) => setFormData({ ...formData, pre_approved_goals: e.target.checked })}
                        className="h-4 w-4 rounded border-gray-300 accent-primary"
                        data-testid="user-pre-approved-goals-input"
                      />
                      <div>
                        <Label htmlFor="pre_approved_goals" className="cursor-pointer font-medium">
                          Pre-Approved Goals
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          User sees library templates instead of the standard Business/People goal framework
                        </p>
                      </div>
                    </div>
                    {formData.pre_approved_goals && (
                      <div className="grid gap-2 mt-3">
                        <Label htmlFor="assigned_template_id">Goal Template</Label>
                        <SearchableSelect
                          value={formData.assigned_template_id || "none"}
                          onValueChange={(value) => setFormData({ ...formData, assigned_template_id: value === "none" ? null : value })}
                          placeholder="Select template"
                          searchPlaceholder="Search templates..."
                          data-testid="user-template-select"
                          options={[
                            { value: "none", label: "Select Template" },
                            ...templates.filter((t) => t.is_active).map((t) => ({ value: t.id, label: t.title })),
                          ]}
                        />
                      </div>
                    )}
                  </div>

                  {/* Department Head Section */}
                  <div className="border-t pt-4 mt-2">
                    <h4 className="text-sm font-medium text-muted-foreground mb-3">Department Head Information</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="grid gap-2">
                        <Label htmlFor="department_head_id">Department Head ID</Label>
                        <Input
                          id="department_head_id"
                          value={formData.department_head_id}
                          onChange={(e) => setFormData({ ...formData, department_head_id: e.target.value })}
                          placeholder="e.g., EMP001"
                          data-testid="user-dept-head-id-input"
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="department_head_name">Department Head Name</Label>
                        <Input
                          id="department_head_name"
                          value={formData.department_head_name}
                          onChange={(e) => setFormData({ ...formData, department_head_name: e.target.value })}
                          placeholder="e.g., Jane Manager"
                          data-testid="user-dept-head-name-input"
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="department_head_email">Department Head Email</Label>
                        <Input
                          id="department_head_email"
                          type="email"
                          value={formData.department_head_email}
                          onChange={(e) => setFormData({ ...formData, department_head_email: e.target.value })}
                          placeholder="e.g., jane@company.com"
                          data-testid="user-dept-head-email-input"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting} data-testid="save-user-btn">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    {editingUser ? 'Update User' : 'Create User'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            {/* Search and Role Row */}
            <div className="flex gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, email, or employee number..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-users-input"
                  />
                </div>
              </div>
              <SearchableSelect
                value={roleFilter}
                onValueChange={setRoleFilter}
                placeholder="Filter by role"
                className="w-48"
                data-testid="role-filter-select"
                options={[
                  { value: "all", label: "All Roles" },
                  { value: "admin", label: "Admin" },
                  { value: "hr", label: "HR" },
                  { value: "manager", label: "Manager" },
                  { value: "employee", label: "Employee" },
                ]}
              />
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">
                  <X className="h-4 w-4 mr-1" />
                  Clear All
                </Button>
              )}
            </div>
            
            {/* Advanced Filters Row */}
            <div className="grid grid-cols-5 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground">Unit</Label>
                <SearchableSelect
                  value={unitFilter}
                  onValueChange={setUnitFilter}
                  placeholder="All"
                  searchPlaceholder="Search units..."
                  className="h-9 mt-1"
                  options={[
                    { value: "all", label: "All Units" },
                    ...businessUnits.map(bu => ({ value: bu.name, label: bu.name })),
                  ]}
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Division</Label>
                <SearchableSelect
                  value={divisionFilter}
                  onValueChange={setDivisionFilter}
                  placeholder="All"
                  searchPlaceholder="Search divisions..."
                  className="h-9 mt-1"
                  options={[
                    { value: "all", label: "All Divisions" },
                    ...divisions.map(d => ({ value: d.name, label: d.name })),
                  ]}
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Department</Label>
                <SearchableSelect
                  value={departmentFilter}
                  onValueChange={setDepartmentFilter}
                  placeholder="All"
                  searchPlaceholder="Search departments..."
                  className="h-9 mt-1"
                  options={[
                    { value: "all", label: "All Departments" },
                    ...departments.map(d => ({ value: d.name, label: d.name })),
                  ]}
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Grade</Label>
                <SearchableSelect
                  value={gradeFilter}
                  onValueChange={setGradeFilter}
                  placeholder="All"
                  searchPlaceholder="Search grades..."
                  className="h-9 mt-1"
                  options={[
                    { value: "all", label: "All Grades" },
                    ...grades.map(g => ({ value: g.name, label: g.name })),
                  ]}
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Category</Label>
                <SearchableSelect
                  value={categoryFilter}
                  onValueChange={setCategoryFilter}
                  placeholder="All"
                  searchPlaceholder="Search categories..."
                  className="h-9 mt-1"
                  options={[
                    { value: "all", label: "All Categories" },
                    ...categories.map(c => ({ value: c.name, label: c.name })),
                  ]}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Users ({filteredUsers.length}{filteredUsers.length !== users.length ? ` of ${users.length}` : ''})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No users found</h3>
              <p className="text-muted-foreground">
                {hasActiveFilters ? 'Try adjusting your filters' : 'Start by adding your first user'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Manager</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id} className="table-row-hover">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-sm font-medium text-primary">
                            {user.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium">{user.name}</p>
                          <p className="text-xs text-muted-foreground font-mono">{user.employee_number}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Badge className={roleColors[user.role]}>
                        {user.role.toUpperCase()}
                      </Badge>
                    </TableCell>
                    <TableCell>{user.business_unit || '-'}</TableCell>
                    <TableCell>{user.department || '-'}</TableCell>
                    <TableCell>{user.manager_name || '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(user)}
                          data-testid={`edit-user-${user.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(user.id)}
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
